import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@/components/ReportSubSection';
import { useSelector, shallowEqual } from 'react-redux';
import { useTracking } from 'react-tracking';
import selector from './selector';
import MultipleVsPeers from './components/MultipleVsPeers';
import MultipleVsIndustry from './components/MultipleVsIndustry';
import KeyValuationMetrics from './components/KeyValuationMetrics';
import SharePriceVsFairValue from './components/SharePriceVsFairValue';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import { CompanyStatements } from '@/constants/statementIds';
import Introduction from '@/pages/CompanyReport/components/Overview/components/IntroductionDeprecated';
import FairRatio from './components/FairRatio';
import type { FAQPage } from 'schema-dts';
import DocumentSchema from '@/components/DocumentSchema';
import PageAnchor from '@/components/PageAnchor';
import { useFlag } from '@/features/unleash';
import {
  VALUATION_KEY_METRICS,
  VALUATION_RATIO_VS_PEERS,
  VALUATION_RATIO_VS_INDUSTRY,
  VALUATION_FAIR_RATIO,
  VALUATION_PRICE_VS_FAIR_RATIO,
  VALUATION_HISTORICAL_TIMESERIES,
  ANALYST_PRICE_TARGETS,
} from '@/constants/anchors';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import Tooltip from '@/components/Tooltip';
import { Term, TooltipTitle } from './styled';
import loadable from '@loadable/component';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import IndustryContextProvider from './components/MultipleVsIndustry/components/IndustryContextProvider';
import { AnalystPriceTargets } from './components/AnalystPriceTargets';
import useValuationDescriptions from './hooks/useValuationDescriptions';
import { VALUATION_STATEMENTS, generateQandA } from './utils';
import { useIsMounted } from '@simplywallst/ui-core';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import { useVisibleSectionsContext } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';

import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import { getStatementChecks } from '../Introduction/utils';
import { SectionSchema } from '@/pages/CompanyReport/schemas';
import useExperimentPeerCompanies from '@/pages/CompanyReport/components/SimilarCompanies/useExperimentPeerCompanies';
import ScreenerLinkButton from '@/pages/CompanyReport/components/ScreenerLinkButton';
import { GridViewOptions } from '@/pages/CompanyReport/constants/constants';
import { useMemo } from 'react';

const HistoricalReportSection = loadable(
  () => import('./components/HistoricalChart')
);

const BrazeContentCard = loadable(
  () => import('@/components/BrazeContentCard')
);
const ValueNextStepsContainer = loadable(
  () => import('./components/ValueNextSteps')
);

interface Props {
  sectionNumber: number;
}

export const Valuation = ({ sectionNumber }: Props) => {
  const { Track } = useTracking({ subject: 'valuation' });
  const flag = useFlag({ name: 'mono-historical-values-pe' });
  const shouldShowHistoricalValuesChart = flag.flagsReady && flag.enabled;

  const { isPristine } = useVisibleSectionsContext();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });

  const statementChecks = useMemo(
    () => getStatementChecks(VALUATION_STATEMENTS, statements),
    [statements]
  );

  // Fetch company industry histogram data
  const { name: multipleName, fullName: multipleFullName } =
    selected.multiples.preferredMultiple;
  const getTerm = useGlossaryTerms();
  const fairRatioTerm = getTerm('fair ratio');
  const {
    valuationDescription,
    vsPeersDescription,
    getVsIndustryDescription,
    vsFairValueDescription,
    fairRatioPreDescription,
    fairRatioDescription,
    fairRatioPostDescription,
    analystPriceTargetsDescription,
  } = useValuationDescriptions();
  const isMounted = useIsMounted();

  const nextStep = useExperimentPeerCompanies({
    originalElement: <ValueNextStepsContainer companyId={selected.companyId} />,
    section: 'valuation',
  });

  return (
    <Track>
      <ReportSection
        data-cy-id="valuation-section"
        sectionNumber={sectionNumber}
      >
        <SectionSchema section="valuation" />
        {isMounted ? null : (
          <DocumentSchema<FAQPage>
            item={{
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                generateQandA(
                  valuationDescription,
                  `Valuation Score ${selected.score}/6`
                ),
                generateQandA(
                  vsPeersDescription,
                  statements
                    ?.IsGoodValueComparingPreferredMultipleToPeersAverageValue
                    ?.description
                ),
                generateQandA(
                  getVsIndustryDescription(multipleName),
                  statements?.IsGoodValueComparingPreferredMultipleToIndustry
                    ?.description
                ),
                generateQandA(
                  fairRatioPreDescription +
                    fairRatioDescription +
                    fairRatioPostDescription,
                  statements?.IsGoodValueComparingRatioToFairRatio?.description
                ),
                generateQandA(
                  vsFairValueDescription,
                  statements?.IsUndervaluedBasedOnDCF?.description
                ),
                generateQandA(
                  analystPriceTargetsDescription,
                  statements?.IsAnalystForecastTrustworthy?.description
                ),
              ],
            }}
          />
        )}
        <Introduction
          scoreName="Valuation"
          title={<CompanySectionTitle title="Valuation" />}
          description={valuationDescription}
          checks={statementChecks}
          section="valuation"
        />
        <BrazeContentCardWrapper>
          <BrazeContentCard
            feedType="company-overview.valuation"
            anchorTarget="_blank"
          />
        </BrazeContentCardWrapper>
        <PageAnchor
          type="sub-section-title"
          id={VALUATION_PRICE_VS_FAIR_RATIO}
        />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title="Share Price vs Fair Value"
          description={vsFairValueDescription}
          bodyRender={() => <SharePriceVsFairValue />}
          statements={
            <>
              <Statement
                title={statements?.IsUndervaluedBasedOnDCF?.title}
                description={
                  <Description
                    statement={statements?.IsUndervaluedBasedOnDCF}
                  />
                }
                value={statements?.IsUndervaluedBasedOnDCF?.value}
                statementId={CompanyStatements.valueStatement1}
              />
              <Statement
                title={statements?.IsHighlyUndervaluedBasedOnDCF?.title}
                description={
                  <Description
                    statement={statements?.IsHighlyUndervaluedBasedOnDCF}
                  />
                }
                value={statements?.IsHighlyUndervaluedBasedOnDCF?.value}
                statementId={CompanyStatements.valueStatement2}
              />
            </>
          }
        />
        <ScreenerLinkButton
          ideaName="established-profitable-and-undervalued"
          gridViewOptionId={
            GridViewOptions.GRID_VIEW_IN_CONTEXT_VALUATION_UNDERVALUED
          }
          subSection={VALUATION_PRICE_VS_FAIR_RATIO}
        />
        <hr />
        <PageAnchor type="sub-section-title" id={VALUATION_KEY_METRICS} />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title="Key Valuation Metric"
          description={`Which metric is best to use when looking at relative valuation for ${selected.tickerSymbol}?`}
          bodyRender={() => <KeyValuationMetrics />}
        />
        <hr />
        <PageAnchor type="sub-section-title" id={VALUATION_RATIO_VS_PEERS} />
        <MultipleVsPeers
          preferredMultiple={multipleName}
          statements={
            <Statement
              title={
                statements
                  ?.IsGoodValueComparingPreferredMultipleToPeersAverageValue
                  ?.title
              }
              description={
                <Description
                  statement={
                    statements?.IsGoodValueComparingPreferredMultipleToPeersAverageValue
                  }
                />
              }
              value={
                statements
                  ?.IsGoodValueComparingPreferredMultipleToPeersAverageValue
                  ?.value
              }
              statementId={CompanyStatements.valuationStatement1}
            />
          }
        />
        <hr />
        {shouldShowHistoricalValuesChart && (
          <>
            <PageAnchor
              type="sub-section-title"
              id={VALUATION_HISTORICAL_TIMESERIES}
            />
            <HistoricalReportSection
              companyId={selected.companyId}
              subSectionTitleElement={subSectionTitleElement}
            />
            <hr />
          </>
        )}
        <PageAnchor type="sub-section-title" id={VALUATION_RATIO_VS_INDUSTRY} />
        <IndustryContextProvider>
          <MultipleVsIndustry
            preferredMultiple={multipleName}
            getVsIndustryDescription={getVsIndustryDescription}
            statements={
              <Statement
                title={
                  statements?.IsGoodValueComparingPreferredMultipleToIndustry
                    ?.title
                }
                description={
                  <Description
                    statement={
                      statements?.IsGoodValueComparingPreferredMultipleToIndustry
                    }
                  />
                }
                value={
                  statements?.IsGoodValueComparingPreferredMultipleToIndustry
                    ?.value
                }
                statementId={CompanyStatements.valuationStatement2}
              />
            }
          />
        </IndustryContextProvider>
        <hr />
        <PageAnchor type="sub-section-title" id={VALUATION_FAIR_RATIO} />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title={`${multipleFullName} Ratio vs Fair Ratio`}
          description={
            <p>
              <span>{fairRatioPreDescription}</span>
              <Tooltip
                openDelay={300}
                light
                title={<TooltipTitle>{fairRatioTerm}</TooltipTitle>}
              >
                <Term>{fairRatioDescription}</Term>
              </Tooltip>
              <span>{fairRatioPostDescription}</span>
            </p>
          }
          bodyRender={() => <FairRatio />}
        />
        <hr />
        <PageAnchor type="sub-section-title" id={ANALYST_PRICE_TARGETS} />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title="Analyst Price Targets"
          description={analystPriceTargetsDescription}
          trackingProps={{ subject: 'chart' }}
          bodyRender={() => (
            <AnalystPriceTargets
              isDataTrustworthy={
                statements?.IsAnalystForecastTrustworthy?.value
              }
            />
          )}
          statements={
            <Statement
              title={statements?.IsAnalystForecastTrustworthy?.title}
              description={
                <Description
                  statement={statements?.IsAnalystForecastTrustworthy}
                />
              }
              value={statements?.IsAnalystForecastTrustworthy?.value}
              statementId={CompanyStatements.valuationStatement7}
            />
          }
        />
        <hr />
        {nextStep}
      </ReportSection>
    </Track>
  );
};
